import React, {
  useCallback, useContext, useState,
} from 'react';
import { Modal } from 'anf-core-react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import BreakpointContext from '../../contexts/BreakpointContext';
import ProductPageContext from '../../contexts/ProductPageContext';
import SearchStoresControls from './SearchStoresControls';
import SearchStoresMessage from './SearchStoresMessage';
import FindAStoreModalPaginationWrapper from './FindAStoreModalPaginationWrapper';

const query = gql`
  query getStores($latlng: LatLng!, $qualifiedSku: String) {
    getStores(latlng: $latlng, qualifiedSku: $qualifiedSku) {
      stores {
        storeNumber
        city
        name
        addressLine
        stateOrProvinceName
        country
        postalCode
        telephone
        language
        fax
        latitude
        longitude
        geoNodeUniqueId
        distance
        popinsEligibleStore {
          value
          Displayable
        }
        fisEligibleStore {
          Displayable
          value
        }
        inventoryAvailability {
          unitOfMeasure
          availableQuantity
          sku
          inventoryStatus
          storeNumber
          availabilityDateTime
          availabilityDateTimeFmt
        }
        onlineStoreId
        countryName
      }
      radius
      totalStores
    }
  }
`;

const initQuery = gql`
  query {
    region
  }
`;

export default function FindAStoreModal({
  openFindAStoreModal, setFindAStoreModalOpen, selectedSku,
}) {
  const breakpoints = useContext(BreakpointContext);
  const ProductPageState = useContext(ProductPageContext);
  const { tmntData } = ProductPageState;
  const [searchedAddress, setSearchedAddress] = useState('');
  const [noResults, setNoResults] = useState(false);
  const [fetchedData, setFetchedData] = useState(false);
  const ModalHeading = <h2 className="modal-heading-title">{tmntData.selectAStore?.value}</h2>;
  const { data: initData } = useQuery(initQuery);
  const region = initData?.region;
  const [getStores, { data, error, loading }] = useLazyQuery(query, { fetchPolicy: 'no-cache' });
  const radius = data?.getStores?.radius;
  const totalStores = data?.getStores?.totalStores ?? 0;

  /* get lat lng from searchedAddress and fetches the stores */
  const fetchStore = useCallback(() => {
    setFetchedData(false);
    if (searchedAddress) {
      const service = new window.google.maps.Geocoder();
      service.geocode({
        region,
        address: searchedAddress,
      }, (results) => {
        setFetchedData(true);
        if (results) {
          setNoResults(false);
          const { location } = results?.[0]?.geometry;
          const latlng = {
            lat: location.lat(),
            lng: location.lng(),
          };
          getStores({ variables: { latlng, qualifiedSku: selectedSku } });
        } else {
          setNoResults(true);
        }
      });
    }
  }, [region, getStores, selectedSku, searchedAddress]);

  return (
    <Modal
      isOpen={openFindAStoreModal}
      isFullscreen={!breakpoints.xsmall}
      id="find-store-modal"
      heading={ModalHeading}
      onClose={() => setFindAStoreModalOpen(false)}
    >
      <div className="scope-1892 find-store-content">
        <SearchStoresControls
          loading={loading}
          searchedAddress={searchedAddress}
          setSearchedAddress={setSearchedAddress}
          onClick={fetchStore}
        />
        <div className="store-map-items">
          <SearchStoresMessage
            radius={radius}
            loading={loading}
            error={error}
            isDataFetched={fetchedData}
            searchedAddress={searchedAddress}
            noResults={noResults}
            totalStores={totalStores}
          />
          {fetchedData && !error && !noResults && totalStores > 0
          && <FindAStoreModalPaginationWrapper getStoresData={data?.getStores} />}
        </div>
      </div>
    </Modal>
  );
}
